import { GridColDef } from '@mui/x-data-grid';

import Box from '@mui/material/Box';

import { AssessmentResponse } from 'utils/api/modules/entities/entities.types';

import RiskMatrix from 'containers/RiskAssessmentsDetails/RiskMatrix/RiskMatrix';

const sharedColumnProps: Omit<GridColDef, 'field'> = {
  align: 'left',
  headerAlign: 'left',
  editable: true,
  disableColumnMenu: true,
  renderCell: (params) => <Box sx={{ whiteSpace: 'pre-wrap', margin: '14px 6px' }}>{params.value}</Box>,
};

export const emptyAssessment: Omit<AssessmentResponse, 'uuid'> & { isNew: boolean } = {
  createdAt: '',
  updatedAt: '',
  assessment: {
    hazard: '',
    harmed: '',
    existingControls: '',
    additionalControls: '',
    likelihoodInitial: 0,
    severityInitial: 0,
    riskScoreInitial: 0,
    likelihoodResidual: 0,
    severityResidual: 0,
    riskScoreResidual: 0,
    actionBy: '',
  },
  isNew: true,
};

const createSimpleValueSetter =
  (field: keyof AssessmentResponse['assessment']) => (value: unknown, row: AssessmentResponse) => {
    const updatedRow = { ...row };
    updatedRow.assessment = { ...updatedRow.assessment, [field]: value };
    return updatedRow;
  };

export const baseColumns: GridColDef[] = [
  {
    ...sharedColumnProps,
    field: 'hazard',
    headerName: 'Hazard',
    width: 150,
    valueGetter: (_value, row: AssessmentResponse) => row.assessment.hazard,
    valueSetter: createSimpleValueSetter('hazard'),
  },
  {
    ...sharedColumnProps,
    field: 'harmed',
    headerName: 'Who might be harmed and how?',
    width: 350,
    valueGetter: (_value, row: AssessmentResponse) => row.assessment.harmed,
    valueSetter: createSimpleValueSetter('harmed'),
  },
  {
    ...sharedColumnProps,
    field: 'existingControls',
    headerName: 'Existing Controls',
    width: 350,
    valueGetter: (_value, row: AssessmentResponse) => row.assessment.existingControls,
    valueSetter: createSimpleValueSetter('existingControls'),
  },
  {
    ...sharedColumnProps,
    field: 'initialRisk',
    headerName: 'Initial Risk Level',
    width: 200,
    valueGetter: (_value, row: AssessmentResponse) => ({
      severityInitial: row.assessment.severityInitial,
      likelihoodInitial: row.assessment.likelihoodInitial,
      riskScoreInitial: row.assessment.riskScoreInitial,
    }),
    valueSetter: (value, row: AssessmentResponse) => {
      const updatedRow = { ...row };
      updatedRow.assessment = {
        ...updatedRow.assessment,
        severityInitial: value.severityInitial,
        likelihoodInitial: value.likelihoodInitial,
        riskScoreInitial: value.riskScoreInitial,
      };
      return updatedRow;
    },
    renderCell: ({ value }) => (
      <RiskMatrix
        editable={false}
        risk={value.riskScoreInitial}
        impact={value.severityInitial}
        likelihood={value.likelihoodInitial}
      />
    ),
    renderEditCell: ({ value, api, id, field }) => (
      <RiskMatrix
        editable
        risk={value.riskScoreInitial}
        impact={value.severityInitial}
        likelihood={value.likelihoodInitial}
        updateRisk={(impact: number, likelihood: number) => {
          api.setEditCellValue({
            id,
            field,
            value: {
              severityInitial: impact,
              likelihoodInitial: likelihood,
              riskScoreInitial: impact * likelihood,
            },
          });
        }}
      />
    ),
  },
  {
    ...sharedColumnProps,
    field: 'additionalControls',
    headerName: 'Additional Controls',
    width: 350,
    valueGetter: (_value, row: AssessmentResponse) => row.assessment.additionalControls,
    valueSetter: createSimpleValueSetter('additionalControls'),
  },
  {
    ...sharedColumnProps,
    field: 'residualRisk',
    headerName: 'Residual Risk',
    width: 200,
    valueGetter: (_value, row: AssessmentResponse) => ({
      severityResidual: row.assessment.severityResidual,
      likelihoodResidual: row.assessment.likelihoodResidual,
      riskScoreResidual: row.assessment.riskScoreResidual,
    }),
    valueSetter: (value, row: AssessmentResponse) => {
      const updatedRow = { ...row };
      updatedRow.assessment = {
        ...updatedRow.assessment,
        severityResidual: value.severityResidual,
        likelihoodResidual: value.likelihoodResidual,
        riskScoreResidual: value.riskScoreResidual,
      };
      return updatedRow;
    },
    renderCell: ({ value }) => (
      <RiskMatrix
        impact={value.severityResidual}
        likelihood={value.likelihoodResidual}
        risk={value.riskScoreResidual}
        editable={false}
      />
    ),
    renderEditCell: (params) => (
      <RiskMatrix
        editable
        risk={params.value.riskScoreResidual}
        impact={params.value.severityResidual}
        likelihood={params.value.likelihoodResidual}
        updateRisk={(impact: number, likelihood: number) => {
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: {
              severityResidual: impact,
              likelihoodResidual: likelihood,
              riskScoreResidual: impact * likelihood,
            },
          });
        }}
      />
    ),
  },
  {
    ...sharedColumnProps,
    field: 'actionBy',
    headerName: 'Action by whom / by when',
    width: 200,
    valueGetter: (_value, row: AssessmentResponse) => row.assessment.actionBy,
    valueSetter: createSimpleValueSetter('actionBy'),
  },
];
